import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, ApiError } from '@/lib/api';

/**
 * "Información adicional" del embarque (Reportes·Operaciones) desde la
 * tarjeta del board. Campos AUTO (Shipment Profile, solo lectura) + campos
 * MANUALES editables. Lo que se guarde aquí se ve también en el reporte.
 */

const AUTO_MAR_AER: [string, string][] = [
  ['shipper', 'Shipper'], ['cnee', 'Cnee'], ['master', 'MBL/MAWB'],
  ['houseRef', 'HBL/HAWB'], ['origenPuerto', 'POL/AOL'], ['destinoPuerto', 'POD/AOD'],
  ['container', 'Container'], ['linea', 'Line'], ['vesselFlight', 'Vessel/Flight'],
];
const AUTO_OTR: [string, string][] = [
  ['master', 'BOL/PRO'], ['origenPuerto', 'Origin'], ['destinoPuerto', 'Destination'],
  ['linea', 'Carrier'],
];
const MAN_MAR_AER: [string, string][] = [
  ['facturacion', 'Facturación'], ['bl', 'BL'], ['locales', 'Locales'],
  ['agente', 'Agente'], ['inland', 'Inland'], ['caat', 'CAAT'], ['seguro', 'Seguro'],
  ['otro', 'Otro'], ['liberacion', 'Liberación'], ['aa', 'AA'],
  ['encomienda', 'Encomienda'], ['garantia', 'Garantía'], ['proveedor', 'Proveedor'],
  ['demoras', 'Demoras'], ['cerrado', 'Cerrado'], ['comments', 'Comments'],
];
const MAN_OTR: [string, string][] = [
  ['citaEntrega', 'Cita de entrega'], ['estadias', 'Estadías'], ['cajaUnidad', 'Caja/Unidad'],
  ['usa', 'USA'], ['transfer', 'Transfer'], ['mex', 'MEX'], ['facturacion', 'Facturación'],
  ['inlandMx', 'Inland MX'], ['transfer2', 'Transfer (USA)'], ['inlandUsa', 'Inland USA'],
  ['locales', 'Locales'], ['seguro', 'Seguro'], ['otro', 'Otro'], ['aa', 'AA'],
  ['cerrado', 'Cerrado'], ['comments', 'Comments'],
];

export function OpsInfoDialog({
  bookingId,
  bookingIdExterno,
  transporte,
  open,
  onOpenChange,
}: {
  bookingId: number;
  bookingIdExterno: string;
  transporte: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const esOtr = transporte?.toUpperCase() === 'ROA';
  const autos = esOtr ? AUTO_OTR : AUTO_MAR_AER;
  const manuales = esOtr ? MAN_OTR : MAN_MAR_AER;

  const infoQ = useQuery({
    queryKey: ['ops-info', bookingId],
    queryFn: () => api<Record<string, unknown>>(`/reports/operaciones/info/${bookingId}`),
    enabled: open,
  });

  const [valores, setValores] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!infoQ.data) return;
    const v: Record<string, string> = {};
    for (const [k] of manuales) v[k] = infoQ.data[k] == null ? '' : String(infoQ.data[k]);
    // autos VACÍOS también se capturan (regla 17-sep)
    for (const [k] of autos) {
      if (infoQ.data[k] == null || infoQ.data[k] === '') v[k] = '';
    }
    setValores(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoQ.data]);

  const guardar = useMutation({
    mutationFn: () =>
      api(`/reports/operaciones/info/${bookingId}`, {
        method: 'PATCH',
        body: JSON.stringify(valores),
      }),
    onSuccess: () => {
      toast.success('Información adicional guardada');
      qc.invalidateQueries({ queryKey: ['ops-info', bookingId] });
      qc.invalidateQueries({ queryKey: ['reporte-operaciones'] });
      onOpenChange(false);
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar'),
  });

  const puedeEditar = infoQ.data?.puedeEditar === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Información adicional · {bookingIdExterno}</DialogTitle>
        </DialogHeader>
        {infoQ.isLoading && <p className="text-sm text-ink-400 py-4">Cargando…</p>}
        {infoQ.data && (
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-400 mb-2">
                Del sistema (CargoWise) — los vacíos se pueden capturar
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {autos.map(([k, label]) => {
                  const vacio = infoQ.data![k] == null || infoQ.data![k] === '';
                  return (
                    <div key={k}>
                      <div className="text-[11px] uppercase text-ink-400">{label}</div>
                      {vacio && puedeEditar ? (
                        <Input
                          value={valores[k] ?? ''}
                          placeholder="Capturar…"
                          onChange={(e) => setValores((v) => ({ ...v, [k]: e.target.value }))}
                        />
                      ) : (
                        <div className="text-sm text-ink-900 break-words">
                          {vacio ? '—' : String(infoQ.data![k])}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">
                Captura de operaciones {puedeEditar ? '' : '(solo lectura para tu rol)'}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {manuales.map(([k, label]) => (
                  <div key={k} className={k === 'comments' ? 'sm:col-span-2' : ''}>
                    <label className="text-[11px] uppercase text-ink-400">{label}</label>
                    <Input
                      value={valores[k] ?? ''}
                      disabled={!puedeEditar}
                      onChange={(e) => setValores((v) => ({ ...v, [k]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>
            {puedeEditar && (
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
                  {guardar.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
